import { useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import {
  ISurveyAnalyticsFilter,
  SURVEY_DIMENSIONS,
  SurveyDimension,
} from './analyticsModel';
import { keepSurveyHubParams } from './surveysHubModel';

/**
 * Analytics state lives in the URL, not in `useState` (spec 0038 revision).
 *
 * Three things were broken while it was component state:
 *  - the browser Back button did not undo a drill-down, so the only escape
 *    from «курс → группа → занятие» was a Reset that wiped everything;
 *  - the screen could not be linked to, which meant the group and teacher
 *    cards had nowhere to send a manager;
 *  - the applied filter was invisible — after drilling into a group, nothing
 *    on screen said WHICH group, because the panel renders no group control.
 *
 * Putting it in the query string fixes all three at once and gives the
 * breadcrumbs something real to remove.
 */

/** Query-param name per filter field. Short, because these end up in links. */
const LIST_PARAMS: Record<string, keyof ISurveyAnalyticsFilter> = {
  course: 'courseIds',
  group: 'groupIds',
  teacher: 'teacherIds',
  survey: 'surveyIds',
  metric: 'metrics',
};

const SCALAR_PARAMS: Record<string, keyof ISurveyAnalyticsFilter> = {
  from: 'dateFrom',
  to: 'dateTo',
};

export interface IAnalyticsDeepLink {
  filter: ISurveyAnalyticsFilter;
  dimension: SurveyDimension;
  tab: 'table' | 'comments';
  setFilter: (next: ISurveyAnalyticsFilter) => void;
  setDimension: (next: SurveyDimension) => void;
  setTab: (next: 'table' | 'comments') => void;
  /** Drill one level: pin the row's key and step the dimension down. */
  drill: (
    filterKey: keyof ISurveyAnalyticsFilter,
    key: string,
    next: SurveyDimension,
  ) => void;
  /** Remove ONE constraint — what «сбросить всё» could never do. */
  clearOne: (field: keyof ISurveyAnalyticsFilter) => void;
  clearAll: () => void;
}

export const useAnalyticsDeepLink = (): IAnalyticsDeepLink => {
  const history = useHistory();
  const { search, pathname } = useLocation();

  const params = useMemo(() => new URLSearchParams(search), [search]);

  const filter = useMemo<ISurveyAnalyticsFilter>(() => {
    const next: ISurveyAnalyticsFilter = {};
    Object.entries(LIST_PARAMS).forEach(([param, field]) => {
      const raw = params.get(param);
      // Comma-separated so a multi-select link stays readable by a human.
      if (raw) (next as Record<string, unknown>)[field] = raw.split(',');
    });
    Object.entries(SCALAR_PARAMS).forEach(([param, field]) => {
      const raw = params.get(param);
      if (raw) (next as Record<string, unknown>)[field] = raw;
    });
    return next;
  }, [params]);

  const dimension = useMemo<SurveyDimension>(() => {
    const raw = params.get('dimension') as SurveyDimension | null;
    return raw && (SURVEY_DIMENSIONS as readonly string[]).includes(raw)
      ? raw
      : 'course';
  }, [params]);

  // ТЗ 0124: `?tab=` теперь принадлежит хабу «Опросы» — своя вкладка
  // аналитики живёт в `?view=` (старые ссылки `tab=comments` переписывает
  // редирект старого маршрута).
  const tab = params.get('view') === 'comments' ? 'comments' : 'table';

  const write = useCallback(
    (
      nextFilter: ISurveyAnalyticsFilter,
      nextDimension: SurveyDimension,
      nextTab: 'table' | 'comments',
      { replace = false }: { replace?: boolean } = {},
    ) => {
      const next = keepSurveyHubParams(params, new URLSearchParams());
      Object.entries(LIST_PARAMS).forEach(([param, field]) => {
        const value = nextFilter[field] as string[] | undefined;
        if (value && value.length) next.set(param, value.join(','));
      });
      Object.entries(SCALAR_PARAMS).forEach(([param, field]) => {
        const value = nextFilter[field] as string | undefined;
        if (value) next.set(param, value);
      });
      if (nextDimension !== 'course') next.set('dimension', nextDimension);
      if (nextTab !== 'table') next.set('view', nextTab);
      const query = next.toString();
      const target = query ? `${pathname}?${query}` : pathname;
      // `push` for a drill-down (Back should undo it), `replace` for tweaking
      // a filter you are already looking at — otherwise Back walks you through
      // every keystroke of a date field.
      if (replace) history.replace(target);
      else history.push(target);
    },
    [history, pathname, params],
  );

  return {
    filter,
    dimension,
    tab,
    setFilter: useCallback(
      (next) => write(next, dimension, tab, { replace: true }),
      [write, dimension, tab],
    ),
    setDimension: useCallback(
      (next) => write(filter, next, tab, { replace: true }),
      [write, filter, tab],
    ),
    setTab: useCallback(
      (next) => write(filter, dimension, next, { replace: true }),
      [write, filter, dimension],
    ),
    drill: useCallback(
      (filterKey, key, next) =>
        write({ ...filter, [filterKey]: [key] }, next, tab),
      [write, filter, tab],
    ),
    clearOne: useCallback(
      (field) => {
        const next = { ...filter };
        delete next[field];
        write(next, dimension, tab, { replace: true });
      },
      [write, filter, dimension, tab],
    ),
    clearAll: useCallback(() => write({}, dimension, tab, { replace: true }), [
      write,
      dimension,
      tab,
    ]),
  };
};
